import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { NumberRange, PaleState, ThunkDispatchFunc } from '@/interfaces';
import { actions } from '@/state/filterActions';
import { fetchResults } from '@/state/resultActions';
import { ActivityFilter } from './checkbox/ActivityFilter';
import { CharterSupplierFilter } from './checkbox/CharterSupplierFilter';
import { FacilityFilter } from './checkbox/FacilityFilter';
import { HotelRatingFilter } from './checkbox/HotelRatingFilter';
import { MealFilter } from './checkbox/MealFilter';
import { NonCharterSupplierFilter } from './checkbox/NonCharterSupplierFilter';
import { FilterGroup } from './FilterGroup';
import { KeywordFilter } from './KeywordFilter';
import { PriceRangeFilter } from './PriceRangeFilter';
import { DistanceSlider } from './slider/DistanceSlider';

export const Filters = () => {
    const dispatch = useDispatch<ThunkDispatchFunc>();

    const priceRange = useSelector((s: PaleState) => s.filters.priceRange);
    const distanceToBeach = useSelector((s: PaleState) => s.filters.distanceToBeach);
    const distanceToCity = useSelector((s: PaleState) => s.filters.distanceToCity);

    const updatePriceRange = useCallback(
        (values?: NumberRange) => {
            dispatch((inner) => {
                inner(actions.filterPriceRange(values));
                return inner(fetchResults());
            });
        },
        [dispatch],
    );

    const triggerFetch = useCallback(() => {
        dispatch(fetchResults());
    }, [dispatch]);

    const handleDistanceToBeachChange = useCallback(
        (value?: number) => {
            dispatch(actions.filterDistanceToBeach(value));
        },
        [dispatch],
    );

    const handleDistanceToCityChange = useCallback(
        (value?: number) => {
            dispatch(actions.filterDistanceToCity(value));
        },
        [dispatch],
    );

    return (
        <>
            <FilterGroup title="Pris per person" testId="pricePerPerson">
                <PriceRangeFilter
                    id="price"
                    legend="Pris per person"
                    labels={['Fra kroner', 'Til kroner']}
                    affix="kr"
                    range={priceRange}
                    updateRange={updatePriceRange}
                />
            </FilterGroup>
            <FilterGroup title="Hotellstandard" testId="hotelStandard">
                <HotelRatingFilter />
            </FilterGroup>
            <FilterGroup title="Avstander" testId="distances" className="space-y-16">
                <DistanceSlider
                    id="distanceToBeachFilter"
                    label="Avstand til strand"
                    min={0}
                    max={5000}
                    initialDistance={distanceToBeach?.[1] ?? undefined}
                    distanceChange={handleDistanceToBeachChange}
                    triggerFetch={triggerFetch}
                />
                <DistanceSlider
                    id="distanceToCityFilter"
                    label="Avstand til sentrum"
                    min={0}
                    max={5000}
                    initialDistance={distanceToCity?.[1] ?? undefined}
                    distanceChange={handleDistanceToCityChange}
                    triggerFetch={triggerFetch}
                />
            </FilterGroup>
            <FilterGroup title="Fasiliteter og tilbud" testId="facilitiesAndOffers" className="space-y-16">
                <MealFilter />
                <FacilityFilter />
                <ActivityFilter />
            </FilterGroup>
            <FilterGroup title="Søk etter..." testId="keywords">
                <KeywordFilter />
            </FilterGroup>
            <FilterGroup title="Tilbydere og selskaper" testId="providers" className="space-y-16">
                <CharterSupplierFilter />
                <NonCharterSupplierFilter />
            </FilterGroup>
        </>
    );
};
